import React from 'react';
import { Box, Typography } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { observer } from 'mobx-react-lite';
import PageInnerContainer from 'pages/components/pageInnerContainer';
import BlockHeader from 'pages/components/blockHeader';
import { useContext, useState } from 'react';
import { borderRadiusValues } from 'shared/lib/utils/uiConstants';
import IconEdit from 'shared/icons/iconEdit';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import { formatBackToFrontDate } from 'shared/lib/utils/globalUtils';
import { downloadFile } from 'app/api/api';
import { FileModel } from 'entities/file/model/fileModel';
import NoPreviewPlug from 'shared/lib/extendedComponents/grids/components/noPreviewPlug';
import EditFileModalController from 'widgets/files/editFileModalController';
import { useNavigate } from 'react-router-dom';
import { useSearchParamsUtils } from 'app/hooks/useSearchParamsUtils';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { useTranslation } from 'react-i18next';
import ReplaceFileModalController from 'widgets/files/replaceFileModalController';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';
import ActionInlineButton from 'shared/lib/components/controls/buttons/actionInlineButton';
import UploadGrid from 'pages/uploadPage/components/uploadGrid';
import { sizeToString } from 'shared/lib/utils/utils';
import FavoritesButton from 'shared/lib/components/controls/buttons/favoritesButton';

export default observer(function ExplorerFile({
  fileModel,
}: {
  fileModel: FileModel;
}) {
  const {
    mobxStore: {
      authStore: { user },
    },
  } = useContext(MobxContext);
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const { t } = useTranslation();
  const { mobxStore } = useContext(MobxContext);
  const { isLessLaptop, isLessTablet } = useBooleanScreens();

  const [editingModel, setEditingModel] = useState<null | FileModel>(null);
  const [replacingModel, setReplacingModel] = useState<null | FileModel>(null);
  const { getFolderSearchParams, getSectionSearchParams } =
    useSearchParamsUtils();
  const navigate = useNavigate();

  const isFavorite = fileModel.data?.is_favorite;

  const toggleFavorite = async () => {
    try {
      const newIsFavorite = !isFavorite;
      await fileModel.setFavorite(newIsFavorite);
    } catch (e) {
      console.error(e);
      mobxStore.notificationsStore.errorMessage = t(
        'notifications.errors.default'
      );
    }
  };

  return fileModel?.data ? (
    <PageInnerContainer>
      <BlockHeader
        sx={{
          gridTemplateColumns: isLessLaptop ? '1fr' : '767fr 393fr',
        }}
        forceBreak
        text={fileModel.data.name}
      ></BlockHeader>

      <UploadGrid isAutoHeight={false}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'start',
            borderRadius: borderRadiusValues.medium,
            maxHeight: { mobile: '50vh', laptop: 'unset' },
            overflow: 'hidden',
          }}
        >
          <Box
            className={isLessLaptop ? '' : 'noscroll'}
            sx={{
              'width': 1,
              'height': 1,
              'display': 'flex',
              'justifyContent': 'start',
              'alignItems': 'start',
              'overflow': 'auto',
              '& .preview-image': {
                objectFit: 'contain',
                width: 1,
                maxWidth: '100%',
                borderRadius: borderRadiusValues.medium,
              },
            }}
          >
            {fileModel.data?.source_preview_url ? (
              <img
                className="preview-image"
                alt=""
                src={`${fileModel.data?.source_preview_url}`}
              ></img>
            ) : (
              <NoPreviewPlug sx={{ width: 1 }}></NoPreviewPlug>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: sp.s100,
          }}
        >
          {isLessLaptop ? (
            <Box></Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {user?.isOnlyView ? null : (
                <ActionInlineButton
                  Icon={IconEdit}
                  onClick={() => setEditingModel(fileModel)}
                >
                  {t('common.edit')}
                </ActionInlineButton>
              )}
              <FavoritesButton
                isFavorite={Boolean(fileModel.data?.is_favorite)}
                onToggle={toggleFavorite}
              ></FavoritesButton>
            </Box>
          )}

          {isLessLaptop && (
            <ButtonsContainer variant={isLessTablet ? 'vertical' : 'full'}>
              <SecondaryButton onClick={toggleFavorite}>
                {isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'}
              </SecondaryButton>
              <PrimaryButton
                onClick={() =>
                  downloadFile({
                    id: fileModel.id,
                    name: fileModel.data?.name || '',
                  })
                }
              >
                Скачать
              </PrimaryButton>
            </ButtonsContainer>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: sp.s20,
            }}
          >
            <Typography
              sx={{ fontSize: 16 }}
            >{`Редактор: ${fileModel.data.username}`}</Typography>
            <Typography
              sx={{ fontSize: 16 }}
            >{`Обновлено: ${formatBackToFrontDate(
              fileModel.data.last_change_date
            )}`}</Typography>
            <Typography
              sx={{ fontSize: 16 }}
            >{`Формат: ${fileModel.data.type.toUpperCase()}`}</Typography>
            <Typography sx={{ fontSize: 16 }}>{`Объем: ${sizeToString(
              fileModel.data.size
            )}`}</Typography>
          </Box>

          {!isLessLaptop && (
            <ButtonsContainer variant="full">
              {user?.isOnlyView ? null : (
                <SecondaryButton onClick={() => setReplacingModel(fileModel)}>
                  Заменить файл
                </SecondaryButton>
              )}
              <PrimaryButton
                onClick={() =>
                  downloadFile({
                    id: fileModel.id,
                    name: fileModel.data?.name || '',
                  })
                }
              >
                Скачать
              </PrimaryButton>
            </ButtonsContainer>
          )}
        </Box>
      </UploadGrid>

      {editingModel?.data && (
        <EditFileModalController
          updatable={editingModel.data}
          handleClose={() => setEditingModel(null)}
          afterFileDelete={(id) => {
            if (fileModel.data?.folder_id && fileModel.data?.section_id) {
              navigate({
                search: getFolderSearchParams({
                  folderId: fileModel.data?.folder_id,
                }),
              });
            } else if (fileModel.data?.section_id) {
              navigate({
                search: getSectionSearchParams({
                  sectionId: fileModel.data?.section_id,
                }),
              });
            }
          }}
          afterFileUpdate={() => {
            fileModel.fetch({ in_trash: null });
            mobxStore.generateExplorerPageKey();
          }}
        ></EditFileModalController>
      )}

      {replacingModel && (
        <ReplaceFileModalController
          fileModel={replacingModel}
          handleClose={() => setReplacingModel(null)}
        ></ReplaceFileModalController>
      )}
    </PageInnerContainer>
  ) : (
    <></>
  );
});
